import { useCallback, useEffect, useRef, useState } from 'react'
import { VerificationModal } from './verification-modal'

type VerificationStatus =
	| 'verifying-server'
	| 'server-success'
	| 'server-failed'
	| 'verify-code-ready'
	| 'verifying-code'
	| 'code-success'
	| 'code-failed'
import { Assets } from 'services/user/assets'
import { useAccountStore, useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { toast } from 'react-toastify'
import { isValidDomain } from 'utils'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { ChatService } from 'services/chat'
import { Onboarding } from 'services/user/onboarding'

// Polling config — matches existing Settings > Tracking Code behavior
const POLL_INTERVAL_MS = 15000 // 15 seconds
const MAX_POLLS = 5 // 5 polls = 75 seconds
const POLL_TIMEOUT_MS = 150000 // 2.5 minutes backup timeout

// Delay before auto-transitioning from server-success to verify-code-ready
const SERVER_SUCCESS_DELAY_MS = 2000

interface VerificationControllerProps {
	open: boolean
	onClose: () => void
	onGoToDashboard?: () => void
	accountId: string
	assetId: string
}

export const VerificationController = ({
	open,
	onClose,
	onGoToDashboard,
	accountId,
	assetId
}: VerificationControllerProps) => {
	const { hasFeature } = useAccountStore(
		useShallow((s) => ({
			hasFeature: s.hasFeature
		}))
	)
	const { asset, setAssetForId } = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			setAssetForId: s.setAssetForId
		}))
	)

	const [status, setStatus] = useState<VerificationStatus>('verify-code-ready')
	const trackPostHogEvent = useTrackPostHogEvent()

	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)
	const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const isMountedRef = useRef(true)

	// Fire-and-forget: persist tracker verification status to setup object
	const saveVerificationStatus = useCallback(
		(
			status: 'success' | 'failed_server_side_tracking' | 'failed_tracking_code'
		) => {
			Onboarding.updateTrackerVerificationStatus(accountId, status).catch(
				() => {
					// Silently ignore — this is a fire-and-forget call
				}
			)
		},
		[accountId]
	)

	// Determine if this is a truly advanced user (has feature AND has configured CNAME)
	// Key insight: setupCompleted only becomes true after the ENTIRE flow is done.
	// Users who did step 1 (added domain, created CNAME) have cnameCreated=true and a subdomain,
	// but setupCompleted is still undefined/false. DNS verification is relevant for them.
	const hasServerSideFeature = hasFeature('server_side_tracking')
	const hasConfiguredCname =
		asset?.server_side_tracking?.cnameCreated === true &&
		!!asset?.server_side_tracking?.subdomain
	const isAdvancedTracking = hasServerSideFeature && hasConfiguredCname

	// Get website domain for verification link
	const websiteDomain = asset?.domain || ''

	// Cleanup all timers
	const cleanupTimers = useCallback(() => {
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current)
			pollingIntervalRef.current = null
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
		if (transitionTimeoutRef.current) {
			clearTimeout(transitionTimeoutRef.current)
			transitionTimeoutRef.current = null
		}
	}, [])

	// Cleanup on unmount
	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
			cleanupTimers()
		}
	}, [cleanupTimers])

	// Set initial status when modal opens
	useEffect(() => {
		if (open) {
			if (isAdvancedTracking) {
				setStatus('verifying-server')
				runDnsVerification()
			} else {
				setStatus('verify-code-ready')
			}
		} else {
			// Reset state when modal closes
			cleanupTimers()
		}
	}, [open])

	// --- DNS Verification (Advanced users with completed setup only) ---
	const runDnsVerification = useCallback(async () => {
		if (!accountId || !assetId) return

		try {
			const result = await Assets.verifyDnsRecord(accountId, assetId)

			if (!isMountedRef.current) return

			if (result.valid) {
				setStatus('server-success')
				// Auto-transition to code verification after brief delay
				transitionTimeoutRef.current = setTimeout(() => {
					if (isMountedRef.current) {
						setStatus('verify-code-ready')
					}
				}, SERVER_SUCCESS_DELAY_MS)
			} else {
				setStatus('server-failed')
				saveVerificationStatus('failed_server_side_tracking')
			}
		} catch (error) {
			console.error('DNS verification error:', error)
			if (isMountedRef.current) {
				setStatus('server-failed')
				saveVerificationStatus('failed_server_side_tracking')
			}
		}
	}, [accountId, assetId])

	// --- Code Verification (shared by all user types) ---
	const runCodeVerification = useCallback(async () => {
		if (!accountId || !assetId) {
			toast.error('Account or asset information is missing')
			return
		}

		if (!websiteDomain || !isValidDomain(websiteDomain)) {
			toast.error(
				'Website domain is invalid. Please check your website settings.'
			)
			return
		}

		setStatus('verifying-code')

		try {
			// Step 1: Start verification — get the param token
			const result = await Assets.startVerification(accountId, assetId)
			const verificationParam = result.param

			// Step 2: Open website with verification param via <a> link approach
			// Using an anchor element to avoid popup blocker warnings
			const url = websiteDomain.startsWith('http')
				? `${websiteDomain}?cgvrfc=${verificationParam}`
				: `https://${websiteDomain}?cgvrfc=${verificationParam}`

			const link = document.createElement('a')
			link.href = url
			link.target = '_blank'
			link.rel = 'noopener noreferrer'
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			// Step 3: Poll for verification result
			let pollCount = 0

			pollingIntervalRef.current = setInterval(async () => {
				try {
					pollCount++
					const pollResult = await Assets.checkVerificationStatus(
						accountId,
						assetId
					)

					if (!isMountedRef.current) return

					if (pollResult.verification?.eventReceived === true) {
						// Success — tracking code was detected
						cleanupTimers()
						setStatus('code-success')
						saveVerificationStatus('success')

						// Refresh asset in store — this updates tc_installed,
						// which causes the banner to auto-hide
						await setAssetForId(accountId, assetId)

						// Check serverSideTrackingUrlDetected for advanced users
						if (
							isAdvancedTracking &&
							pollResult.verification?.serverSideTrackingUrlDetected === false
						) {
							toast.info(
								'Standard tracking code detected. Consider upgrading to advanced tracking for full protection.'
							)
						}

						trackPostHogEvent(
							PostHogEvent.INACTIVE_TC_VERIFICATION_SUCCESS,
							PostHogPath.DASHBOARD,
							{
								is_advanced: isAdvancedTracking,
								server_side_detected:
									pollResult.verification?.serverSideTrackingUrlDetected ?? null
							}
						)

						toast.success(
							'Verification successful! The tracking code is installed.'
						)
					} else if (pollCount >= MAX_POLLS) {
						// Max polls reached — timeout
						cleanupTimers()
						if (isMountedRef.current) {
							setStatus('code-failed')
							saveVerificationStatus('failed_tracking_code')
							trackPostHogEvent(
								PostHogEvent.INACTIVE_TC_VERIFICATION_FAILED,
								PostHogPath.DASHBOARD,
								{ reason: 'poll_timeout', is_advanced: isAdvancedTracking }
							)
							toast.error(
								'Verification failed. Please make sure the tracking code is installed on your website.'
							)
						}
					}
				} catch (error) {
					console.error('Error checking verification status:', error)
					if (pollCount >= MAX_POLLS) {
						cleanupTimers()
						if (isMountedRef.current) {
							setStatus('code-failed')
							saveVerificationStatus('failed_tracking_code')
							toast.error(
								'Verification failed. Please make sure the tracking code is installed on your website.'
							)
						}
					}
				}
			}, POLL_INTERVAL_MS)

			// Backup timeout (2.5 minutes)
			timeoutRef.current = setTimeout(() => {
				cleanupTimers()
				if (isMountedRef.current) {
					setStatus('code-failed')
					saveVerificationStatus('failed_tracking_code')
					trackPostHogEvent(
						PostHogEvent.INACTIVE_TC_VERIFICATION_FAILED,
						PostHogPath.DASHBOARD,
						{ reason: 'backup_timeout', is_advanced: isAdvancedTracking }
					)
					toast.error(
						'Verification timed out. Please make sure the tracking code is installed on your website.'
					)
				}
			}, POLL_TIMEOUT_MS)
		} catch (error) {
			console.error('Start verification error:', error)
			if (isMountedRef.current) {
				setStatus('code-failed')
				saveVerificationStatus('failed_tracking_code')
				trackPostHogEvent(
					PostHogEvent.INACTIVE_TC_VERIFICATION_FAILED,
					PostHogPath.DASHBOARD,
					{ reason: 'start_error', is_advanced: isAdvancedTracking }
				)
				toast.error('Failed to start verification. Please try again.')
			}
		}
	}, [
		accountId,
		assetId,
		websiteDomain,
		isAdvancedTracking,
		cleanupTimers,
		setAssetForId
	])

	// --- Action handlers ---
	const [isNavigating, setIsNavigating] = useState(false)

	const handleGoToDashboard = useCallback(async () => {
		setIsNavigating(true)
		cleanupTimers()
		if (onGoToDashboard) {
			onGoToDashboard()
		} else {
			onClose()
		}
	}, [cleanupTimers, onClose, onGoToDashboard])

	const handleContactSupport = useCallback(() => {
		ChatService.open()
	}, [])

	const handleVerifyCode = useCallback(() => {
		runCodeVerification()
	}, [runCodeVerification])

	const handleClose = useCallback(() => {
		cleanupTimers()
		onClose()
	}, [cleanupTimers, onClose])

	return (
		<VerificationModal
			open={open}
			status={status}
			onGoToDashboard={handleGoToDashboard}
			onContactSupport={handleContactSupport}
			onVerifyCode={handleVerifyCode}
			onClose={handleClose}
			isNavigating={isNavigating}
		/>
	)
}
